import React from "react";
import { useDebounce } from "usehooks-ts";
import { usersAPI } from "../shared/api";
import { useNavigate } from "react-router-dom";
import { UsersView } from "./components/UsersView";

import type { User } from "../shared/types/models";

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [showActiveOnly, setShowActiveOnly] = React.useState(() => {
    const stored = localStorage.getItem('users-show-active-only');
    return stored !== null ? stored === 'true' : true;
  });
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  function navigateToUserDetails(user: User): void {
    navigate(`/users/${user.id}`);
  }

  function handleToggleChange(checked: boolean): void {
    setShowActiveOnly(checked);
    localStorage.setItem('users-show-active-only', checked.toString());
  }

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>): void {
    setSearchTerm(event.target.value);
  }

  function fuzzyMatch(text: string, searchTerm: string): boolean {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const textLower = text.toLowerCase();
    
    // Simple fuzzy matching - contains search term
    return textLower.includes(searchLower);
  }

  const { data: users, isLoading: isLoadingUsers } =
    usersAPI.endpoints.findUsers.useQuery();

  const filteredUsers = React.useMemo(() => {
    if (!users) return [];
    
    let filtered = users;
    
    // Filter by active status
    if (showActiveOnly) {
      filtered = filtered.filter(user => user.status === 'active');
    }
    
    // Filter by search term
    if (debouncedSearchTerm.trim()) {
      filtered = filtered.filter(user => {
        const fullName = `${user.firstName} ${user.lastName}`;
        return fuzzyMatch(fullName, debouncedSearchTerm) || 
               fuzzyMatch(user.emailAddress, debouncedSearchTerm);
      });
    }
    
    return filtered;
  }, [users, showActiveOnly, debouncedSearchTerm]);

  return (
    <UsersView
      users={filteredUsers}
      isLoadingUsers={isLoadingUsers}
      navigateToUserDetails={navigateToUserDetails}
      showActiveOnly={showActiveOnly}
      onToggleChange={handleToggleChange}
      searchTerm={searchTerm}
      debouncedSearchTerm={debouncedSearchTerm}
      onSearchChange={handleSearchChange}
    />
  );
};

export default UsersPage;
